/**
 * Mirror of the backend `AiAuditReport` (api-v2 `QpienAi/core/aiMessages.types.ts`). The GraphQL
 * field is a `JSON` scalar because the shape is the LLM response schema, so the panel declares the
 * contract here and validates defensively at the boundary.
 */

export const AI_AUDIT_VERDICTS = [
  "CORRECT",
  "PARTIALLY_CORRECT",
  "WRONG",
  "UNDETERMINED"
] as const

export type AiAuditVerdict = (typeof AI_AUDIT_VERDICTS)[number]

export type AiAuditConfidence = "HIGH" | "MEDIUM" | "LOW"

export type AiAuditProblemSeverity = "BLOCKER" | "WARNING" | "INFO"

export interface AiAuditEvidence {
  quote: string
  source: string
  impact?: string
}

export interface AiAuditProblem {
  severity: AiAuditProblemSeverity
  detail: string
  rootStep?: string
}

export interface AiAuditReport {
  verdict: AiAuditVerdict
  confidence: AiAuditConfidence
  headline: string
  decision: string
  because: string[]
  evidence: AiAuditEvidence[]
  problems: AiAuditProblem[]
  fix: string | null
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function asText(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

/**
 * The backend already normalizes, but the field is an untyped JSON scalar — a stale server or a
 * partial payload must degrade to a readable card rather than crash the dialog.
 */
export function parseAuditReport(raw: unknown): AiAuditReport | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null

  const record = raw as Record<string, unknown>
  const verdict = String(record.verdict ?? "").toUpperCase()
  const confidence = String(record.confidence ?? "").toUpperCase()

  return {
    verdict: (AI_AUDIT_VERDICTS as ReadonlyArray<string>).includes(verdict)
      ? (verdict as AiAuditVerdict)
      : "UNDETERMINED",
    confidence: ["HIGH", "MEDIUM", "LOW"].includes(confidence)
      ? (confidence as AiAuditConfidence)
      : "MEDIUM",
    headline: asText(record.headline),
    decision: asText(record.decision),
    because: asArray<unknown>(record.because).map(asText).filter(Boolean),
    evidence: asArray<Record<string, unknown>>(record.evidence)
      .filter((item) => item && typeof item === "object" && asText(item.quote))
      .map((item) => ({
        quote: asText(item.quote),
        source: asText(item.source) || "veri",
        impact: asText(item.impact) || undefined
      })),
    problems: asArray<Record<string, unknown>>(record.problems)
      .filter((item) => item && typeof item === "object" && asText(item.detail))
      .map((item) => {
        const severity = String(item.severity ?? "").toUpperCase()
        return {
          severity: ["BLOCKER", "WARNING", "INFO"].includes(severity)
            ? (severity as AiAuditProblemSeverity)
            : "WARNING",
          detail: asText(item.detail),
          rootStep: asText(item.rootStep) || undefined
        }
      }),
    fix: asText(record.fix) || null
  }
}
